/**
 * Sharing the tax iceberg: the copy and image behind the page's Open Graph / Twitter cards and
 * its Share button. Everything here is derived from the URL state so a shared link unfurls with
 * the numbers the sender was looking at. Hand-branched per locale like seo-copy.ts.
 */
import { calculateTaxIceberg, type TaxIcebergInput, type TaxIcebergResult } from './calculate-tax-iceberg';
import { FINNISH_MUNICIPAL_TAX_RATES_2026 } from './finnish-municipal-tax-rates-2026';
import { PARISH_TAX_AVG_RATE_PCT, churchTaxRateFor } from './finnish-parish-tax-rates-2026';
import { solveGrossForNet } from './solve-gross-for-net';
import { DEFAULT_TAX_ICEBERG_STATE, POPULAR_MONTHLY_SALARIES, asTaxIcebergLocale, type TaxIcebergLocale, type TaxIcebergUrlState } from './tax-iceberg-url';

/** National average parish rate, used for church members when no municipality is chosen (with a
 *  municipality, its own parish rate applies: finnish-parish-tax-rates-2026.ts). */
export const CHURCH_TAX_AVG_RATE_PCT = PARISH_TAX_AVG_RATE_PCT;

/** The calculator inputs a URL state stands for (money in the state is in its own period). In
 *  net mode the gross is solved from the target net with the same options. */
export function taxIcebergInputsForState(state: TaxIcebergUrlState): TaxIcebergInput {
  const toAnnual = (v: number) => (state.period === 'year' ? v : v * 12);
  const hasMunicipality = Object.prototype.hasOwnProperty.call(FINNISH_MUNICIPAL_TAX_RATES_2026, state.municipality);
  const municipalTaxRatePct = hasMunicipality ? FINNISH_MUNICIPAL_TAX_RATES_2026[state.municipality] : undefined;
  const options: Omit<TaxIcebergInput, 'grossAnnual'> = {
    municipalTaxRatePct,
    churchTaxRatePct: state.churchMember ? churchTaxRateFor(hasMunicipality ? state.municipality : undefined) : undefined,
    age: state.age,
    dependentChildren: state.dependentChildren,
    isSoleGuardian: state.dependentChildren > 0 && state.isSoleGuardian,
    employerSize: state.employerSize,
    unionFees: toAnnual(state.unionFees),
    commutingCosts: toAnnual(state.commuting),
    dividends: toAnnual(state.dividends),
    capitalGains: toAnnual(state.capitalGains),
    unlistedDividends: toAnnual(state.unlistedDividends),
    unlistedNetAssets: state.unlistedNetAssets > 0 ? state.unlistedNetAssets : undefined,
    includeVat: state.includeVat,
  };
  const grossAnnual = state.mode === 'net' ? solveGrossForNet(toAnnual(state.gross), options) : toAnnual(state.gross);
  return { grossAnnual, ...options };
}

/** The numbers a share card quotes, in the period the sender was viewing. */
export interface ShareFacts {
  locale: TaxIcebergLocale;
  perYear: boolean;
  /** True when the sender asked for a net and the gross was solved: the copy leads with that. */
  fromNet: boolean;
  gross: number;
  net: number;
  trueEmployerCost: number;
  /** Taxes plus employee contributions as a share of gross. */
  totalRatePct: number;
}

export function shareFactsFromResult(locale: string | undefined, result: TaxIcebergResult, grossAnnual: number, perYear: boolean, fromNet = false): ShareFacts {
  const div = perYear ? 1 : 12;
  return {
    locale: asTaxIcebergLocale(locale),
    perYear,
    fromNet,
    gross: grossAnnual / div,
    net: result.salary.net / div,
    trueEmployerCost: result.salary.trueEmployerCost / div,
    totalRatePct: result.breakdown.employee.effectiveRatePct,
  };
}

export function shareFactsForState(locale: string | undefined, state: TaxIcebergUrlState): ShareFacts | null {
  const inputs = taxIcebergInputsForState(state);
  if (!(inputs.grossAnnual > 0)) return null;
  return shareFactsFromResult(locale, calculateTaxIceberg(inputs), inputs.grossAnnual, state.period === 'year', state.mode === 'net');
}

function money(locale: TaxIcebergLocale, amount: number): string {
  return new Intl.NumberFormat(locale === 'fi' ? 'fi-FI' : locale === 'sv' ? 'sv-SE' : 'en-IE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(amount);
}
function pct(locale: TaxIcebergLocale, value: number): string {
  return new Intl.NumberFormat(locale === 'fi' ? 'fi-FI' : locale === 'sv' ? 'sv-SE' : 'en-IE', { style: 'percent', maximumFractionDigits: 0 }).format(value / 100);
}
/** "/kk", "/mån", "a month" style period suffixes. */
function per(locale: TaxIcebergLocale, perYear: boolean, long = false): string {
  if (locale === 'fi') return long ? (perYear ? 'vuodessa' : 'kuukaudessa') : perYear ? '/v' : '/kk';
  if (locale === 'sv') return long ? (perYear ? 'om året' : 'i månaden') : perYear ? '/år' : '/mån';
  if (long) return perYear ? 'a year' : 'a month';
  return perYear ? '/year' : '/month';
}

/** Title and text for the social card and the share sheet: the sender's own numbers, then the
 *  hook. Titles stay near 60 characters (link previews cut around there); the text fits X and a
 *  WhatsApp preview. */
export function shareCopy(facts: ShareFacts): { title: string; text: string } {
  const { locale } = facts;
  const gross = money(locale, facts.gross);
  const net = money(locale, facts.net);
  const cost = money(locale, facts.trueEmployerCost);
  const rate = pct(locale, facts.totalRatePct);
  if (facts.fromNet) {
    if (locale === 'fi') {
      return {
        title: `${net} käteen ${per(locale, facts.perYear, true)} vaatii ${gross} bruttopalkan. Minne loput katoavat?`,
        text: `Verot ja maksut vievät ${rate}. Työnantajasi maksaa ${cost}${per(locale, facts.perYear)}, jotta sinulle jää ${net}. Katso koko verojäävuori, pinnan päällä ja alla.`,
      };
    }
    if (locale === 'sv') {
      return {
        title: `${net} i handen ${per(locale, facts.perYear, true)} kräver ${gross} brutto. Vart tar resten vägen?`,
        text: `Skatter och avgifter tar ${rate}. Din arbetsgivare betalar ${cost}${per(locale, facts.perYear)} för att du ska få ${net}. Se hela skatteisberget, över och under ytan.`,
      };
    }
    return {
      title: `${net} in hand ${per(locale, facts.perYear, true)} takes ${gross} gross. Where does the rest go?`,
      text: `Taxes and contributions take ${rate}. Your employer pays ${cost} ${per(locale, facts.perYear, true)} so you can take home ${net}. See the whole tax iceberg, above and below the waterline.`,
    };
  }
  if (locale === 'fi') {
    return {
      title: `${gross} brutto, ${net} käteen ${per(locale, facts.perYear, true)}. Minne loput katoavat?`,
      text: `Verot ja maksut vievät ${rate}. Työnantajasi maksaa ${cost}${per(locale, facts.perYear)}, jotta sinulle jää ${net}. Katso koko verojäävuori, pinnan päällä ja alla.`,
    };
  }
  if (locale === 'sv') {
    return {
      title: `${gross} brutto, ${net} i handen ${per(locale, facts.perYear, true)}. Vart tar resten vägen?`,
      text: `Skatter och avgifter tar ${rate}. Din arbetsgivare betalar ${cost}${per(locale, facts.perYear)} för att du ska få ${net}. Se hela skatteisberget, över och under ytan.`,
    };
  }
  return {
    title: `${gross} gross, ${net} in hand ${per(locale, facts.perYear, true)}. Where does the rest go?`,
    text: `Taxes and contributions take ${rate}. Your employer pays ${cost} ${per(locale, facts.perYear, true)} so you can take home ${net}. See the whole tax iceberg, above and below the waterline.`,
  };
}

/** Copy for the calculator itself, when no salary is being quoted (the generic share image). */
export function genericShareCopy(locale: string | undefined): { title: string; text: string } {
  const l = asTaxIcebergLocale(locale);
  if (l === 'fi') return { title: 'Verojäävuori', text: 'Kuinka suuri osa palkastasi uppoaa pinnan alle?' };
  if (l === 'sv') return { title: 'Skatteisberget', text: 'Hur stor del av din lön sjunker under ytan?' };
  return { title: 'The Tax Iceberg', text: 'How much of your pay sinks below the waterline?' };
}

/** What the 1200x630 share image says. Lines are short on purpose: SVG text does not wrap. */
export interface ShareImageLines {
  kicker: string;
  headline: string;
  /** Second big line (the net figure), salary images only. */
  subline: string | null;
  chips: string[];
}

export function shareImageLines(locale: string | undefined, facts: ShareFacts | null): ShareImageLines {
  const l = asTaxIcebergLocale(locale);
  const brand = l === 'fi' ? 'Verojäävuori' : l === 'sv' ? 'Skatteisberget' : 'Tax iceberg';
  if (!facts) {
    const generic = genericShareCopy(l);
    return { kicker: `${l === 'fi' ? 'Verolaskuri' : l === 'sv' ? 'Skatteräknare' : 'Tax calculator'} · jobcrawls.com`, headline: generic.title, subline: generic.text, chips: [] };
  }
  const gross = money(l, facts.gross);
  const net = money(l, facts.net);
  const cost = money(l, facts.trueEmployerCost);
  const rate = pct(l, facts.totalRatePct);
  const p = per(l, facts.perYear);
  if (l === 'fi') {
    return { kicker: `${brand} · jobcrawls.com`, headline: `${gross}${p} brutto`, subline: `${net} käteen`, chips: [`Verot ja maksut ${rate}`, `Työnantajalle ${cost}${p}`] };
  }
  if (l === 'sv') {
    return { kicker: `${brand} · jobcrawls.com`, headline: `${gross}${p} brutto`, subline: `${net} i handen`, chips: [`Skatter och avgifter ${rate}`, `Arbetsgivaren betalar ${cost}${p}`] };
  }
  return { kicker: `${brand} · jobcrawls.com`, headline: `${gross}${p} gross`, subline: `${net} in hand`, chips: [`Taxes and contributions ${rate}`, `Employer pays ${cost}${p}`] };
}

export const SHARE_IMAGE_DIR = '/resources/share';

/** Path of the pre-rendered share image for a state: the salary-specific card when the view is
 *  one of the popular monthly salaries with nothing exotic on top (those are rendered ahead of
 *  time by scripts/render-tax-iceberg-share-images.tsx), else the locale's generic card. The
 *  generic card is deliberate for anything customised: a card quoting numbers that do not match
 *  the page would be worse than one with none. */
export function shareImagePath(locale: string | undefined, state: TaxIcebergUrlState | null): string {
  const l = asTaxIcebergLocale(locale);
  if (state && isPrerenderedShareState(state)) return `${SHARE_IMAGE_DIR}/tax-iceberg-${l}-${state.gross}.png`;
  return `${SHARE_IMAGE_DIR}/tax-iceberg-${l}.png`;
}

export function isPrerenderedShareState(state: TaxIcebergUrlState): boolean {
  const d = DEFAULT_TAX_ICEBERG_STATE;
  return (
    state.period === 'month' &&
    state.mode === 'gross' &&
    state.taxCardRate === undefined &&
    POPULAR_MONTHLY_SALARIES.includes(state.gross) &&
    state.municipality === d.municipality &&
    state.employerSize === d.employerSize &&
    state.includeVat === d.includeVat &&
    state.churchMember === d.churchMember &&
    state.age === d.age &&
    state.dependentChildren === 0 &&
    state.unionFees === 0 &&
    state.commuting === 0 &&
    state.dividends === 0 &&
    state.capitalGains === 0 &&
    state.unlistedDividends === 0 &&
    state.compare.length === 0
  );
}

/** Every share image the render script produces, as (locale, gross | null) pairs. */
export function prerenderedShareImages(): Array<{ locale: TaxIcebergLocale; gross: number | null; path: string }> {
  const out: Array<{ locale: TaxIcebergLocale; gross: number | null; path: string }> = [];
  for (const locale of ['en', 'fi', 'sv'] as const) {
    out.push({ locale, gross: null, path: shareImagePath(locale, null) });
    for (const gross of POPULAR_MONTHLY_SALARIES) out.push({ locale, gross, path: shareImagePath(locale, { ...DEFAULT_TAX_ICEBERG_STATE, gross }) });
  }
  return out;
}
